import {
  InputFieldValidation,
  QuestionnaireComponentKind,
  QuestionnaireFormComponentRegistry,
} from '@main/dynamic-form';
import { DynamicFormErrorMessage } from '@main/dynamic-form/react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type Kind = Exclude<QuestionnaireComponentKind, QuestionnaireComponentKind.Section>;

const requiredKey = {
  [QuestionnaireComponentKind.Text]: 'questionnaireValidation.required.text',
  [QuestionnaireComponentKind.Number]: 'questionnaireValidation.required.number',
  [QuestionnaireComponentKind.Date]: 'questionnaireValidation.required.date',
  [QuestionnaireComponentKind.Email]: 'questionnaireValidation.required.email',
  [QuestionnaireComponentKind.Url]: 'questionnaireValidation.required.url',
  [QuestionnaireComponentKind.Options]: 'questionnaireValidation.required.option',
  [QuestionnaireComponentKind.File]: 'questionnaireValidation.required.file',
} as const satisfies Record<Kind, string>;

/** The message an answer shows for the rule it fails, restating the rule in the reader's language. */
export function useQuestionnaireErrorMessage(): DynamicFormErrorMessage<QuestionnaireFormComponentRegistry> {
  const { t } = useTranslation('ui');

  return useCallback(
    (error, config) => {
      if (error.message) {
        return error.message;
      }
      if (config.kind === QuestionnaireComponentKind.Section) {
        return undefined;
      }
      // Only text kinds carry limits; the others read as unset.
      const validation: InputFieldValidation | undefined = config.validation;

      switch (error.type) {
        case 'required':
          return config.kind === QuestionnaireComponentKind.Options && config.isMultiOptions
            ? t('questionnaireValidation.required.options')
            : t(requiredKey[config.kind]);
        case 'pattern':
          return config.kind === QuestionnaireComponentKind.Email
            ? t('questionnaireValidation.pattern.email')
            : config.kind === QuestionnaireComponentKind.Url
              ? t('questionnaireValidation.pattern.url')
              : t('questionnaireValidation.pattern.other');
        // Limits are numeric, and a date input compares only a string limit, so only numbers reach these.
        case 'min':
          return t('questionnaireValidation.min', { min: validation?.min });
        case 'max':
          return t('questionnaireValidation.max', { max: validation?.max });
        case 'minLength':
          return t('questionnaireValidation.minLength', { count: validation?.minLength });
        case 'maxLength':
          return t('questionnaireValidation.maxLength', { count: validation?.maxLength });
        default:
          return t('questionnaireValidation.invalid');
      }
    },
    [t],
  );
}
